
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { SubscriptionTier } from "./useSubscriptionUpdate";

interface PricingTierProps {
  name: string;
  description: string;
  price: number;
  features: string[];
  tier: SubscriptionTier;
  currentTier: SubscriptionTier;
  isUpdating: boolean;
  onUpgrade: (tier: SubscriptionTier) => void;
  gradientFrom: string;
  gradientTo: string;
}

export const PricingTier = ({
  name,
  description,
  price,
  features,
  tier,
  currentTier,
  isUpdating,
  onUpgrade,
  gradientFrom,
  gradientTo
}: PricingTierProps) => {
  const buttonText = currentTier === tier 
    ? 'Current Plan' 
    : tier === 'free' 
      ? 'Downgrade to Free'
      : `${TIER_LEVELS[currentTier] > TIER_LEVELS[tier] ? 'Downgrade' : 'Upgrade'} to ${name}`;

  return (
    <div className="relative group">
      <div className={`absolute -inset-0.5 bg-gradient-to-r from-${gradientFrom} to-${gradientTo} rounded-lg blur opacity-30 group-hover:opacity-100 transition duration-1000 group-hover:duration-200`}></div>
      <div className="relative p-6 space-y-6 bg-white rounded-lg border border-gray-200">
        <div className="space-y-2">
          <h3 className="text-2xl font-bold">{name}</h3>
          <p className="text-gray-500 text-sm">{description}</p>
          <div className="text-3xl font-bold">
            ${price}{price > 0 && <span className="text-lg font-normal">/mo</span>}
          </div>
        </div>
        <ul className="space-y-3">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center text-sm">
              <Check className="h-4 w-4 text-green-500 mr-2" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        <Button 
          className={`w-full bg-gradient-to-r from-${gradientFrom} to-${gradientTo}`}
          onClick={() => onUpgrade(tier)}
          disabled={currentTier === tier || isUpdating}
        >
          {buttonText}
        </Button>
      </div>
    </div>
  );
};

const TIER_LEVELS: Record<SubscriptionTier, number> = {
  free: 0,
  basic: 1,
  growth: 2,
  professional: 3
};
